import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Text,
} from "@chakra-ui/react";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import {
  deleteAnswerDraft,
  fetchAnswerDraft,
  modifyAnswer,
  saveAnswerDraft,
  SessionExpiredError,
  uploadAnswer,
} from "../api";
import { exportAnswerToWord } from "../utils/answerWord";
import { createAnswerSheetElement } from "../utils/answerPdf";
import useAnswerDuration from "../hooks/useAnswerDuration";
import { normalizeAnswerMetadata } from "../constants/answerMetadata";

const LEGACY_DRAFT_KEY = "answer-draft";
const LEGACY_DRAFT_TOKEN_KEY = "answer-draft-token";
const DRAFT_TOKEN_KEY_PREFIX = "answer-draft-token:";
const NEW_DRAFT_SESSION_KEY = "answer-draft-document-key";
const AUTOSAVE_DEBOUNCE_MS = 4000;
const AUTOSAVE_MAX_INTERVAL_MS = 30000;

const createDraftId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const getInitialDraftKey = (answerId) => {
  if (answerId) return `answer:${answerId}`;

  const existingKey = sessionStorage.getItem(NEW_DRAFT_SESSION_KEY);
  if (existingKey) return existingKey;

  const nextKey = `new:${createDraftId()}`;
  sessionStorage.setItem(NEW_DRAFT_SESSION_KEY, nextKey);
  return nextKey;
};

const getDraftTokenStorageKey = (draftKey) =>
  `${DRAFT_TOKEN_KEY_PREFIX}${draftKey}`;

const readDurationCheckpoint = (draftKey) => {
  try { return Number(sessionStorage.getItem(`answer-duration:${draftKey}`)) || 0; }
  catch { return 0; }
};

const getStoredDraftToken = (draftKey) =>
  localStorage.getItem(getDraftTokenStorageKey(draftKey)) ||
  localStorage.getItem(LEGACY_DRAFT_TOKEN_KEY);

const sanitizeAnswerHtml = (rawHtml) => {
  const wrapper = document.createElement("div");
  wrapper.innerHTML = rawHtml || "";

  const allowedAligns = ["left", "center", "right", "justify"];

  Array.from(wrapper.querySelectorAll("*")).forEach((el) => {
    const tagName = el.tagName;

    if (tagName !== "DIV" && tagName !== "BR") {
      el.replaceWith(...el.childNodes);
      return;
    }

    if (tagName === "DIV") {
      const styleTextAlign = el.style.textAlign;
      const attrAlign = el.getAttribute("align");

      const textAlign = allowedAligns.includes(styleTextAlign)
        ? styleTextAlign
        : allowedAligns.includes(attrAlign)
          ? attrAlign
          : "";

      Array.from(el.attributes).forEach((attr) => {
        el.removeAttribute(attr.name);
      });

      if (textAlign) {
        el.style.textAlign = textAlign;
      }
    }

    if (tagName === "BR") {
      Array.from(el.attributes).forEach((attr) => {
        el.removeAttribute(attr.name);
      });
    }
  });

  return wrapper.innerHTML;
};

const Controls = ({
  togglePreviewMode,
  isPreviewMode,
  textRef,
  editorRef,
  subject,
  editVersion,
  answer_id,
  onAnswerCreated,
  templateType,
  onDraftMetadataLoaded,
  metadata,
  initialDuration,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();

  const printRef = useRef(null);

  const [hasDraft, setHasDraft] = useState(false);
  const [draft, setDraft] = useState(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState("idle");
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [persistedAnswerId, setPersistedAnswerId] = useState(answer_id || null);
  const [draftKey, setDraftKey] = useState(() => getInitialDraftKey(answer_id));
  const draftKeyRef = useRef(draftKey);
  const draftTokenRef = useRef(getStoredDraftToken(draftKey));
  const draftVersionRef = useRef(null);
  const lastDraftSignatureRef = useRef(null);
  const draftSavePromiseRef = useRef(Promise.resolve());
  const autosaveTimerRef = useRef(null);
  const lastEditorRef = useRef(null);
  const draftReadyRef = useRef(false);
  const draftDecisionPendingRef = useRef(false);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const metadataRef = useRef(metadata);
  const { getSeconds, resumeFrom } = useAnswerDuration(Math.max(initialDuration || 0, readDurationCheckpoint(draftKey)));
  const checkpointDuration = useCallback(() => {
    try { sessionStorage.setItem(`answer-duration:${draftKeyRef.current}`, String(getSeconds())); }
    catch { /* Storage may be unavailable; the server draft is still attempted. */ }
  }, [getSeconds]);
  const getSaveMetadata = useCallback(() => ({
    ...normalizeAnswerMetadata(metadataRef.current),
    duration_seconds: getSeconds(),
  }), [getSeconds]);
  const subjectRef = useRef(subject);
  const templateTypeRef = useRef(templateType);
  const hasAuthenticatedSession = Boolean(
    localStorage.getItem("access_token") ||
    localStorage.getItem("refresh_token"),
  );

  useEffect(() => {
    if (answer_id) {
      setPersistedAnswerId(answer_id);
      const nextDraftKey = `answer:${answer_id}`;
      if (draftKeyRef.current !== nextDraftKey) {
        draftKeyRef.current = nextDraftKey;
        draftTokenRef.current = getStoredDraftToken(nextDraftKey);
        draftVersionRef.current = null;
        setDraftKey(nextDraftKey);
      }
    }
  }, [answer_id]);

  useEffect(() => {
    draftKeyRef.current = draftKey;
  }, [draftKey]);

  useEffect(() => {
    subjectRef.current = subject;
    templateTypeRef.current = templateType;
    metadataRef.current = metadata;
  }, [subject, templateType, metadata]);

  const getEditorElement = useCallback(() => {
    if (editorRef.current?.classList?.contains("editor")) {
      return editorRef.current;
    }

    const nestedEditor = editorRef.current?.querySelector?.(".page.editor");
    if (nestedEditor) return nestedEditor;

    const nestedEditorFromTextRef =
      textRef.current?.querySelector?.(".page.editor");
    if (nestedEditorFromTextRef) return nestedEditorFromTextRef;

    return document.querySelector(".page.editor");
  }, [editorRef, textRef]);

  const persistDraft = useCallback(
    (nextDraft, { keepalive = false, allowPending = false } = {}) => {
      setDraftSaveStatus("saving");
      const requestDraftKey = draftKeyRef.current;
      const requestDraftToken = draftTokenRef.current;

      const saveRequest = async () => {
        if (draftDecisionPendingRef.current && !allowPending) {
          const error = new Error(
            "다른 탭 또는 기기의 최신 임시저장을 먼저 확인해주세요.",
          );
          error.code = "draft_conflict";
          setDraftSaveStatus("conflict");
          throw error;
        }

        if (!nextDraft.content && !nextDraft.subject_code && !nextDraft.is_past_exam) {
          await deleteAnswerDraft({
            authenticated: hasAuthenticatedSession,
            draftToken: requestDraftToken,
            draftKey: requestDraftKey,
            keepalive,
          });

          if (draftKeyRef.current === requestDraftKey) {
            localStorage.removeItem(getDraftTokenStorageKey(requestDraftKey));
            localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
            draftTokenRef.current = null;
            draftVersionRef.current = null;
            lastDraftSignatureRef.current = JSON.stringify(nextDraft);
            dirtyRef.current = false;
            setDraft(null);
            setHasDraft(false);
            setDraftSaveStatus("saved");
          }
          return { exists: false, draft: null };
        }

        let result;
        try {
          result = await saveAnswerDraft(
            {
              ...nextDraft,
              base_version: draftVersionRef.current,
            },
            {
              authenticated: hasAuthenticatedSession,
              draftToken: requestDraftToken,
              draftKey: requestDraftKey,
              keepalive,
            },
          );
        } catch (error) {
          if (
            error.code === "draft_conflict" &&
            error.draft &&
            draftKeyRef.current === requestDraftKey
          ) {
            draftVersionRef.current = error.draft.version || null;
            draftDecisionPendingRef.current = true;
            setDraft(error.draft);
            setHasDraft(true);
            setDraftSaveStatus("conflict");
          }
          throw error;
        }

        if (result?.draft_token && draftKeyRef.current === requestDraftKey) {
          draftTokenRef.current = result.draft_token;
          localStorage.setItem(
            getDraftTokenStorageKey(requestDraftKey),
            result.draft_token,
          );
          localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
        } else if (
          hasAuthenticatedSession &&
          result?.owner === "account" &&
          draftKeyRef.current === requestDraftKey
        ) {
          draftTokenRef.current = null;
          localStorage.removeItem(getDraftTokenStorageKey(requestDraftKey));
          localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
        }

        localStorage.removeItem(LEGACY_DRAFT_KEY);
        if (draftKeyRef.current === requestDraftKey) {
          draftVersionRef.current = result?.draft?.version || null;
          lastDraftSignatureRef.current = JSON.stringify(nextDraft);
          dirtyRef.current = false;
          setDraft(nextDraft);
          setHasDraft(true);
          setDraftSaveStatus("saved");
        }
        return result;
      };

      if (keepalive) return saveRequest();

      draftSavePromiseRef.current = draftSavePromiseRef.current
        .catch(() => undefined)
        .then(saveRequest);
      return draftSavePromiseRef.current;
    },
    [hasAuthenticatedSession],
  );

  useEffect(() => {
    let active = true;
    draftReadyRef.current = false;
    draftDecisionPendingRef.current = false;

    const loadServerDraft = async () => {
      let serverDraft = null;

      try {
        const result = await fetchAnswerDraft({
          authenticated: hasAuthenticatedSession,
          draftToken: draftTokenRef.current,
          draftKey,
        });

        if (!active) return;

        if (result?.exists) {
          serverDraft = result.draft;
          resumeFrom(serverDraft.duration_seconds);
          draftVersionRef.current = serverDraft.version || null;
          setDraft(serverDraft);
          setHasDraft(true);
          setDraftSaveStatus("saved");
          draftDecisionPendingRef.current = true;
          lastDraftSignatureRef.current = JSON.stringify({
            subject: serverDraft.subject || "",
            content: serverDraft.content,
            content_html: serverDraft.content_html || "",
            template_type: serverDraft.template_type || templateTypeRef.current,
          });

          if (result.owner === "anonymous" && draftTokenRef.current) {
            localStorage.setItem(
              getDraftTokenStorageKey(draftKey),
              draftTokenRef.current,
            );
            localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
          }
        }

        if (hasAuthenticatedSession && result?.owner === "account") {
          draftTokenRef.current = null;
          localStorage.removeItem(getDraftTokenStorageKey(draftKey));
          localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
        }
      } catch (error) {
        console.error("서버 임시저장 불러오기 실패:", error);
        if (active) setDraftSaveStatus("error");
      }

      if (serverDraft || !active) {
        draftReadyRef.current = active;
        return;
      }

      const legacySaved = localStorage.getItem(LEGACY_DRAFT_KEY);
      if (!legacySaved) {
        const editorEl = getEditorElement();
        if (editorEl) {
          const baseline = {
            subject: subjectRef.current || "",
            content:
              editorEl.innerText?.replace(/\n{3,}/g, "\n\n").trim() || "",
            content_html: sanitizeAnswerHtml(editorEl.innerHTML),
            template_type: templateTypeRef.current,
            ...getSaveMetadata(),
          };
          lastDraftSignatureRef.current = JSON.stringify(baseline);
        }
        draftReadyRef.current = true;
        return;
      }

      let legacyDraft;
      try {
        legacyDraft = JSON.parse(legacySaved);
      } catch {
        legacyDraft = { content: legacySaved, content_html: "" };
      }

      const migratedDraft = {
        subject: subjectRef.current || "",
        content: legacyDraft.content || "",
        content_html: legacyDraft.content_html || "",
        template_type: templateTypeRef.current,
      };
      setDraft(migratedDraft);
      setHasDraft(Boolean(migratedDraft.content));
      draftDecisionPendingRef.current = Boolean(migratedDraft.content);
      draftReadyRef.current = true;

      if (migratedDraft.content) {
        persistDraft(migratedDraft, { allowPending: true }).catch((error) => {
          console.error("기존 임시저장 서버 이전 실패:", error);
          if (active) setDraftSaveStatus("error");
        });
      }
    };

    loadServerDraft();
    return () => {
      active = false;
    };
  }, [
    draftKey,
    getEditorElement,
    hasAuthenticatedSession,
    getSaveMetadata,
    resumeFrom,
    persistDraft,
  ]);

  const buildPrintContent = async () => {
    const editorEl = getEditorElement();

    if (!editorEl || !printRef.current) {
      console.error("프린트할 에디터 또는 printRef를 찾지 못했습니다.");
      return;
    }

    const root = await createAnswerSheetElement({
      html: sanitizeAnswerHtml(editorEl.innerHTML),
      subject,
      templateType,
    });
    root.style.position = "static";
    root.style.left = "auto";
    root.style.top = "auto";
    printRef.current.replaceChildren(root);
  };

  const buildCurrentDraft = useCallback(() => {
    // React can detach refs before passive-effect cleanup on route changes.
    const editorEl = getEditorElement() || lastEditorRef.current;
    if (!editorEl) return null;

    return {
      subject: subjectRef.current || "",
      content: editorEl.innerText?.replace(/\n{3,}/g, "\n\n").trim() || "",
      content_html: sanitizeAnswerHtml(editorEl.innerHTML),
      template_type: templateTypeRef.current,
      ...getSaveMetadata(),
    };
  }, [getEditorElement, getSaveMetadata]);

  const flushCurrentDraft = useCallback(
    ({ keepalive = false } = {}) => {
      checkpointDuration();
      if (!draftReadyRef.current || draftDecisionPendingRef.current || savingRef.current) {
        return Promise.resolve(null);
      }

      const nextDraft = buildCurrentDraft();
      if (!nextDraft) return Promise.resolve(null);

      const signature = JSON.stringify(nextDraft);
      if (signature === lastDraftSignatureRef.current) {
        dirtyRef.current = false;
        return Promise.resolve(null);
      }

      return persistDraft(nextDraft, { keepalive });
    },
    [buildCurrentDraft, persistDraft, checkpointDuration],
  );

  useEffect(() => {
    const editorEl = getEditorElement();
    if (!editorEl) return undefined;
    lastEditorRef.current = editorEl;

    const scheduleAutosave = () => {
      dirtyRef.current = true;
      if (draftDecisionPendingRef.current) {
        draftDecisionPendingRef.current = false;
        setDraft(null);
        setHasDraft(false);
      }

      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = window.setTimeout(() => {
        flushCurrentDraft().catch((error) => {
          console.error("서버 임시저장 실패:", error);
          if (error.code !== "draft_conflict") {
            setDraftSaveStatus("error");
          }
        });
      }, AUTOSAVE_DEBOUNCE_MS);
    };

    const flushForPageExit = () => {
      window.clearTimeout(autosaveTimerRef.current);
      flushCurrentDraft({ keepalive: true }).catch((error) => {
        console.error("페이지 이탈 전 서버 임시저장 실패:", error);
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") flushForPageExit();
    };

    const maxInterval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      flushCurrentDraft().catch((error) => {
        console.error("서버 임시저장 실패:", error);
        if (error.code !== "draft_conflict") {
          setDraftSaveStatus("error");
        }
      });
    }, AUTOSAVE_MAX_INTERVAL_MS);

    editorEl.addEventListener("input", scheduleAutosave);
    editorEl.addEventListener("answer-metadata-change", scheduleAutosave);
    window.addEventListener("pagehide", flushForPageExit);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      editorEl.removeEventListener("input", scheduleAutosave);
      editorEl.removeEventListener("answer-metadata-change", scheduleAutosave);
      window.removeEventListener("pagehide", flushForPageExit);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearTimeout(autosaveTimerRef.current);
      window.clearInterval(maxInterval);
      flushForPageExit();
    };
  }, [flushCurrentDraft, getEditorElement]);

  useEffect(() => {
    // Use the editor's existing debounce path for classification-only edits.
    if (draftReadyRef.current) {
      getEditorElement()?.dispatchEvent(new Event("answer-metadata-change"));
    }
  }, [metadata, templateType, getEditorElement]);

  const loadDraft = () => {
    if (!draft) {
      alert("임시저장된 답안이 없습니다.");
      setHasDraft(false);
      return;
    }

    const preview =
      draft.content?.length > 200
        ? draft.content.slice(0, 200) + "..."
        : draft.content || "분류정보 및 작성시간";

    const confirmed = window.confirm(
      `임시저장된 답안입니다:\n\n${preview}\n\n이 내용을 불러오시겠습니까?`,
    );

    if (!confirmed) return;

    draftDecisionPendingRef.current = false;
    dirtyRef.current = false;
    onDraftMetadataLoaded?.(draft);
    resumeFrom(draft.duration_seconds);

    const editorEl = getEditorElement();

    if (editorEl) {
      if (draft.content_html) {
        editorEl.innerHTML = sanitizeAnswerHtml(draft.content_html);
      } else {
        editorEl.innerText = draft.content;
      }
      editorEl.dispatchEvent(new Event("input", { bubbles: true }));
    }
    setDraft(null);
    setHasDraft(false);
  };

  const saveAnswer = async () => {
    if (savingRef.current) return;

    const editorEl = getEditorElement();

    if (!editorEl) {
      console.error("실제 답안 에디터를 찾지 못했습니다.");
      return;
    }

    const rawHtml = editorEl.innerHTML;

    const content = editorEl.innerText?.replace(/\n{3,}/g, "\n\n").trim() || "";

    if (!content) {
      console.error("내용을 입력해주세요.");
      return;
    }

    if (draftDecisionPendingRef.current) {
      window.alert(
        "다른 탭 또는 기기의 임시저장이 있습니다. 임시저장을 불러오거나 현재 답안을 한 번 더 편집한 뒤 저장해주세요.",
      );
      return;
    }

    const cleanHtml = sanitizeAnswerHtml(rawHtml);
    const savedMetadata = getSaveMetadata();

    setIsSaving(true);
    savingRef.current = true;
    window.clearTimeout(autosaveTimerRef.current);
    let draftPersisted = false;
    let draftSaveError = null;

    try {
      try {
        await persistDraft({
          subject: subject || "",
          content,
          content_html: cleanHtml,
          template_type: templateType,
          ...savedMetadata,
        });
        draftPersisted = true;
      } catch (error) {
        draftSaveError = error;
        console.error("최종 저장 전 임시저장 실패:", error);
        if (error.code !== "draft_conflict") {
          setDraftSaveStatus("error");
        }
        if (
          error instanceof SessionExpiredError ||
          error.code === "draft_conflict"
        ) {
          throw error;
        }
      }

      const targetAnswerId = persistedAnswerId || answer_id;
      const currentDraftKey = draftKeyRef.current;

      if (targetAnswerId || editVersion) {
        if (!targetAnswerId) {
          throw new Error("수정할 답안 ID를 찾지 못했습니다.");
        }

        const response = await modifyAnswer(
          targetAnswerId,
          content,
          cleanHtml,
          currentDraftKey,
          savedMetadata,
        );

        if (!response.ok) {
          throw new Error("Failed to update the answer");
        }

        const data = await response.json();

        setPersistedAnswerId(data.answer_id || targetAnswerId);

        setDraft(null);
        setHasDraft(false);
        localStorage.removeItem(LEGACY_DRAFT_KEY);
        localStorage.removeItem(getDraftTokenStorageKey(currentDraftKey));
        localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
        draftTokenRef.current = null;
        draftVersionRef.current = null;
        lastDraftSignatureRef.current = JSON.stringify({
          subject: subject || "",
          content,
          content_html: cleanHtml,
          template_type: templateType,
          ...savedMetadata,
        });
        dirtyRef.current = false;
        onOpen();

        return data;
      } else {
        const response = await uploadAnswer(
          subject,
          content,
          cleanHtml,
          templateType,
          currentDraftKey,
          savedMetadata,
        );

        if (!response.ok) {
          throw new Error("Failed to save the answer");
        }

        const data = await response.json();

        if (!data.answer_id) {
          throw new Error("저장된 답안 ID를 받지 못했습니다.");
        }

        setPersistedAnswerId(data.answer_id);
        const nextDraftKey = `answer:${data.answer_id}`;
        localStorage.removeItem(getDraftTokenStorageKey(currentDraftKey));
        localStorage.removeItem(LEGACY_DRAFT_TOKEN_KEY);
        sessionStorage.removeItem(NEW_DRAFT_SESSION_KEY);
        draftKeyRef.current = nextDraftKey;
        draftTokenRef.current = getStoredDraftToken(nextDraftKey);
        draftVersionRef.current = null;
        setDraftKey(nextDraftKey);
        onAnswerCreated?.(data);
        setDraft(null);
        setHasDraft(false);
        localStorage.removeItem(LEGACY_DRAFT_KEY);
        lastDraftSignatureRef.current = JSON.stringify({
          subject: subject || "",
          content,
          content_html: cleanHtml,
          template_type: templateType,
          ...savedMetadata,
        });
        dirtyRef.current = false;
        onOpen();

        return data;
      }
    } catch (error) {
      console.error("답안 저장 실패:", error);

      if (error.code === "draft_conflict") {
        window.alert(
          "다른 탭 또는 기기의 최신 임시저장을 확인한 뒤 다시 저장해주세요.",
        );
      } else if (!(error instanceof SessionExpiredError)) {
        window.alert(
          draftPersisted
            ? "답안 저장에 실패했습니다. 작성 내용은 서버에 임시저장되었습니다."
            : draftSaveError
              ? "답안 저장에 실패했고 임시저장도 완료되지 않았습니다. 네트워크 상태를 확인해주세요."
              : "답안을 서버에 저장하지 못했습니다. 네트워크 상태를 확인해주세요.",
        );
      }

      return null;
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleMainPageRedirect = () => {
    navigate("/answers");
  };

  const handleWordExport = async () => {
    const editorEl = getEditorElement();

    if (!editorEl) {
      window.alert("내보낼 답안을 찾지 못했습니다.");
      return;
    }

    setIsExportingWord(true);

    try {
      await exportAnswerToWord({
        html: sanitizeAnswerHtml(editorEl.innerHTML),
        subject,
        templateType,
      });
    } catch (error) {
      console.error("Word 파일 내보내기 실패:", error);
      window.alert("Word 파일을 만드는 데 실패했습니다.");
    } finally {
      setIsExportingWord(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    onBeforeGetContent: async () => {
      await buildPrintContent();

      return new Promise((resolve) => {
        setTimeout(resolve, 100);
      });
    },
  });

  return (
    <>
      <Text role="status" fontSize="xs" alignSelf="center" mr="2">
        {{ saving: "임시저장 중", saved: "임시저장됨", error: "임시저장 실패 · 수동 저장을 시도해주세요", conflict: "다른 탭의 최신 임시저장을 확인해주세요." }[draftSaveStatus] || ""}
      </Text>
      <Button
        onClick={togglePreviewMode}
        colorScheme=""
        bg="#003664"
        mr="2"
        height="40px"
      >
        {isPreviewMode ? "편집 모드로 돌아가기" : "답안 미리보기"}
      </Button>

      {hasDraft && (
        <Button
          onClick={loadDraft}
          colorScheme=""
          bg="#003664"
          mr="2"
          height="40px"
        >
          임시저장 불러오기
        </Button>
      )}

      {/* <Button
        onClick={handleWordExport}
        colorScheme="blue"
        variant="outline"
        bg="white"
        mr="2"
        height="40px"
        isLoading={isExportingWord}
        loadingText="Word 생성 중"
      >
        Word로 저장
      </Button> */}

      {!hasAuthenticatedSession ? (
        <Button colorScheme="" bg="#003664" mr="3" onClick={handlePrint}>
          답안 프린트하기
        </Button>
      ) : (
        <>
          <Button
            colorScheme=""
            bg="#003664"
            height="40px"
            onClick={saveAnswer}
            isLoading={isSaving}
            loadingText="저장 중"
          >
            답안 저장
          </Button>

          <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>답안이 저장되었습니다.</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Text>답안이 성공적으로 저장되었습니다.</Text>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="blue" mr="3" onClick={handlePrint}>
                  답안 프린트하기
                </Button>

                <Button colorScheme="teal" onClick={handleMainPageRedirect}>
                  메인 화면으로 돌아가기
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}

      <div ref={printRef} className="light-print-root" />

      <style>
        {`
          .light-print-root {
            position: fixed;
            left: -10000px;
            top: 0;
            width: 794px;
            opacity: 0;
            pointer-events: none;
            background: white;
            z-index: -1;
          }

          @media print {
            @page {
              size: A4 portrait;
              margin: 0;
            }

            html,
            body {
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
            }

            .light-print-root {
              position: static !important;
              left: auto !important;
              top: auto !important;
              width: 100% !important;
              opacity: 1 !important;
              pointer-events: auto !important;
              z-index: auto !important;
              background: white !important;
            }

            [data-answer-export-page] {
              break-after: page !important;
              page-break-after: always !important;
            }

            [data-answer-export-page]:last-child {
              break-after: auto !important;
              page-break-after: auto !important;
            }
          }
        `}
      </style>
    </>
  );
};

export default Controls;
